// Per-pixel coloring for single-band float/class COGs (Front-End.md §A7 render branch).
// Why this exists: maplibre-cog-protocol treats a bare `cog://url` as RGB imagery, so a
// single-band COG (all our layers: ssurgo/cdl class indices, topo/ndvi/climate floats)
// renders as BLACK garbage. The manifest's render metadata (`categories` | `colormap` +
// `value_min`/`value_max`, `nodata`) must be applied per pixel via the protocol's
// setColorFunction. Pure module (no MapLibre / no protocol import) so it's unit-testable
// the signature is structurally compatible with the library's ColorFunction.
import type { ManifestLayer } from "../types/contract";
import {
  absoluteColorStops,
  paintValueExpression,
  rgbAtStops,
} from "./absoluteColorScale";

interface CogMeta {
  noData?: number;
  offset?: number;
  scale?: number;
}
export type CogColorFn = (
  pixel: ArrayLike<number>,
  color: Uint8ClampedArray,
  metadata: CogMeta,
) => void;

export const AGRO_RAMP = [
  "#a50026",
  "#f46d43",
  "#fee08b",
  "#a6d96a",
  "#1a9850",
]; // RdYlGn: worse → better

// The layer must carry SYMMETRIC value_min/max (−R.
export const DIVERGING_RAMP = [
  "#0571b0",
  "#92c5de",
  "#f7f7f7",
  "#f4a582",
  "#ca0020",
];
const DIVERGING_COLORMAPS = new Set(["rdbu", "diverging"]);
export function isDivergingLayer(
  layer: Pick<ManifestLayer, "colormap">,
): boolean {
  return (
    layer.colormap != null &&
    DIVERGING_COLORMAPS.has(layer.colormap.toLowerCase())
  );
}
// The map-folder subcategory async files variance layers under, and the FE keys the diverging ramp off
// (DB migration 035 exposes NO colormap column — the FE derives it from category='supply' + a variance signal).
export const VARIANCE_SUBCATEGORY = "Variance vs samples";
// Any `supply` VARIANCE layer (signed deviation vs soil test) → diverging. Matched robustly so it fires for BOTH
// the sample-corrected and the assumed-Ca/Mg forks whatever exact subcategory/key async uses (`_variance`).
export function divergingColormapFor(cov: {
  category?: string;
  subcategory?: string;
  key?: string;
}): string | undefined {
  if (cov.category !== "supply") return undefined;
  // the 0–100 PERCENTILE display twin of a variance layer — `…_variance_pct` is a SEQUENTIAL index
  // not a signed deviation (its neutral point is ~60, not 50), so it must NOT get the diverging
  // ramp.
  if (/_pct$/i.test(cov.key ?? "")) return undefined;
  const isVariance =
    cov.subcategory === VARIANCE_SUBCATEGORY ||
    /variance/i.test(cov.subcategory ?? "") ||
    /(^|_)variance($|_)/i.test(cov.key ?? "");
  return isVariance ? "rdbu" : undefined;
}

const LOWER_BETTER_PATTERNS = [
  /^fert-/, // fertilizer/lime NEED: a high rate = a deficient (worse) spot
  /^autorx-/, // served AutoRx surfaces: same as fert-* — MORE fertilizer = red, less = green.
  /^spread-/, // spread PRODUCT rates: same logic — more product = red, less = green.
  // erosion/compaction/salinity magnitudes + moisture DEFICIT (drier = worse). Gradient `slope` is
  // worse-when-steep, but "slope POSITION" (a landform index, not a quality) must NOT invert — the
  // negative lookahead keeps rsp/"relative slope position" on the default higher-is-better ramp.
  /slope(?!\s*position)|rough|erod|kfactor|ls_factor|salin|sodic|bulk[_\s]?dens|deficit|aridit/i,
  /(^|[_\s])(spi|sti|tri|bd|lep|vpd)([_\s]|$)/i, // erosion indices / compaction / shrink-swell / vapor deficit
  /(^|[_\s])(heat|freeze|dry)/i, // stress-day counts + dry spells ("wheat" stays unmatched)
];
type LayerIdTitle = Pick<
  ManifestLayer,
  | "id"
  | "title"
  | "direction"
  | "colormap"
  | "unit"
  | "rgb"
  | "categories"
  | "stretch"
>;
export function lowerIsBetter(layer: LayerIdTitle): boolean {
  // AUTHORITATIVE first — Back-End-Light CV1 `direction`: a covariate layer carries its real
  // agronomic good-direction, so trust it over the name heuristic.
  if (layer.direction === "lower") return true;
  if (layer.direction === "higher") return false;
  const hay = `${layer.id} ${layer.title ?? ""}`;
  return LOWER_BETTER_PATTERNS.some((re) => re.test(hay));
}
// The stops a numeric layer paints with. A DIVERGING (signed-deviation) layer uses the fixed blue→white→red
// scale and ignores agronomic direction; an authored absolute scale uses its own (possibly bipolar) colors
// every other numeric layer uses red→green, reversed where lower is better.
export function rampStopsFor(layer: LayerIdTitle): string[] {
  // exaggerated: lowest red → highest green, whatever the metric's direction or ramp kind
  if (layer.stretch) return AGRO_RAMP;
  if (isDivergingLayer(layer)) return DIVERGING_RAMP;
  const abs = absoluteColorStops(layer);
  if (abs && abs.length >= 2) return abs.map((s) => s.color);
  return lowerIsBetter(layer) ? [...AGRO_RAMP].reverse() : AGRO_RAMP;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  const n = parseInt(
    h.length === 3
      ? h
          .split("")
          .map((c) => c + c)
          .join("")
      : h,
    16,
  );
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// CSS gradient for the Legend — same stops as the map pixels, so they can't disagree.
// Absolute scales place each color at the VALUE's position on the bar (green is not
// always the right edge — bipolar pH peaks in the middle, then goes to blue).
export function rampCss(layer: LayerIdTitle): string {
  const abs = !isDivergingLayer(layer) ? absoluteColorStops(layer) : null;
  if (abs && abs.length >= 2) {
    const lo = abs[0]!.value;
    const hi = abs[abs.length - 1]!.value;
    const span = hi - lo || 1;
    return `linear-gradient(to right, ${abs.map((s) => `${s.color} ${((s.value - lo) / span) * 100}%`).join(", ")})`;
  }
  return `linear-gradient(to right, ${rampStopsFor(layer).join(", ")})`;
}

// 256-entry RGB LUT interpolated across the layer's ramp. Absolute scales map the LUT
// across the authored value range (not the field's min..max).
export function buildLut(layer: LayerIdTitle): Uint8ClampedArray {
  const abs = !isDivergingLayer(layer) ? absoluteColorStops(layer) : null;
  if (abs && abs.length >= 2) {
    const lut = new Uint8ClampedArray(256 * 3);
    const lo = abs[0]!.value;
    const hi = abs[abs.length - 1]!.value;
    const span = hi - lo || 1;
    for (let i = 0; i < 256; i++) {
      const [r, g, b] = rgbAtStops(abs, lo + (span * i) / 255);
      lut[i * 3] = r;
      lut[i * 3 + 1] = g;
      lut[i * 3 + 2] = b;
    }
    return lut;
  }
  const stops = rampStopsFor(layer).map(hexToRgb);
  const lut = new Uint8ClampedArray(256 * 3);
  const segs = stops.length - 1;
  for (let i = 0; i < 256; i++) {
    const t = (i / 255) * segs;
    const s = Math.min(Math.floor(t), segs - 1);
    const f = t - s;
    for (let ch = 0; ch < 3; ch++)
      lut[i * 3 + ch] = stops[s][ch] + (stops[s + 1][ch] - stops[s][ch]) * f;
  }
  return lut;
}

const TRANSPARENT = [0, 0, 0, 0];

// ---- Gridded (per-cell GeoJSON) covariates — choropleth paint expressions -------------
// A covariate served as vector features with a numeric `value` property renders as a
// value-colored fill using the SAME ramps/categories as the COG path. Returns a MapLibre
// style expression (plain JSON array — pure, testable).
export function choroplethFillColor(layer: ManifestLayer): unknown[] {
  // true-color (bare-soil color): each feature carries a baked sRGB `color` — paint it directly, no ramp
  if (layer.rgb) return ["coalesce", ["get", "color"], "rgba(0,0,0,0)"];
  if (layer.categories && layer.categories.length > 0) {
    const branches: unknown[] = [];
    for (const c of layer.categories) branches.push(c.class_value, c.color);
    return [
      "match",
      ["round", ["to-number", ["get", "value"], -1]],
      ...branches,
      "rgba(0,0,0,0)",
    ];
  }
  const abs = absoluteColorStops(layer);
  if (abs && abs.length >= 2) {
    const pairs: unknown[] = [];
    for (const s of abs) pairs.push(s.value, s.color);
    return [
      "interpolate",
      ["linear"],
      paintValueExpression(abs[0]!.value),
      ...pairs,
    ];
  }
  const stops = rampStopsFor(layer);
  const min = layer.value_min ?? 0;
  const max = layer.value_max ?? 1;
  const span = max - min || 1; // degenerate range -> still strictly ascending stop inputs
  const pairs: unknown[] = [];
  for (let i = 0; i < stops.length; i++)
    pairs.push(min + (span * i) / (stops.length - 1), stops[i]);
  return ["interpolate", ["linear"], paintValueExpression(min), ...pairs];
}

// True when a vector/geojson layer carries render metadata => draw it as a choropleth
// (value-colored fills), not as boundary-style linework.
export function isGriddedCovariate(layer: ManifestLayer): boolean {
  const vector = layer.kind === "geojson" || layer.kind === "vector";
  return (
    vector &&
    (layer.rgb === true ||
      (layer.categories?.length ?? 0) > 0 ||
      layer.colormap != null ||
      (layer.value_min != null && layer.value_max != null))
  );
}

/* * A layer the map's exaggerate toggle can restretch: a continuous value ramp (not classes, not true color). */
export function canStretch(layer: ManifestLayer): boolean {
  if (layer.rgb || (layer.categories?.length ?? 0) > 0) return false;
  return layer.kind === "cog" || isGriddedCovariate(layer);
}

/* * min/max of the value the choropleth paints (`value_stored` when present, else `value`), or null. */
function inlineValueExtent(
  inline: unknown,
): { min: number; max: number } | null {
  const feats = (
    inline as {
      features?: { properties?: Record<string, unknown> | null }[];
    } | null
  )?.features;
  if (!Array.isArray(feats)) return null;
  let min = Infinity;
  let max = -Infinity;
  for (const f of feats) {
    const p = f?.properties;
    if (!p) continue;
    const v = "value_stored" in p ? p.value_stored : p.value;
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    if (v < min) min = v;
    if (v > max) max = v;
  }
  return min <= max ? { min, max } : null;
}

/*
 *
 * The copy of a layer the map draws while exaggerate is on: its own lowest value red, highest green.
 * Inline layers take their range from the features themselves; COGs from the raster's served min/max.
 */
export function stretchedLayer(layer: ManifestLayer): ManifestLayer {
  if (!canStretch(layer)) return layer;
  const ext = inlineValueExtent(layer.inline);
  return {
    ...layer,
    stretch: true,
    ...(ext ? { value_min: ext.min, value_max: ext.max } : {}),
  };
}

// Build the per-pixel color function for one manifest layer. Called for EVERY pixel of
// every tile — precompute lookups, keep the hot path branch-light.
export function makeCogColorFunction(layer: ManifestLayer): CogColorFn {
  const layerNodata = layer.nodata;

  // Categorical (ssurgo mukey, CDL): exact class_value -> color; anything else transparent.
  if (layer.categories && layer.categories.length > 0) {
    const classRgb = new Map<number, [number, number, number]>();
    for (const c of layer.categories)
      classRgb.set(c.class_value, hexToRgb(c.color));
    return (pixel, color, metadata) => {
      const v = pixel[0];
      const nd = metadata.noData ?? layerNodata;
      if (v == null || Number.isNaN(v) || (nd !== undefined && v === nd))
        return void color.set(TRANSPARENT);
      const rgb = classRgb.get(Math.round(v));
      if (!rgb) return void color.set(TRANSPARENT); // unknown class -> transparent, not black
      color[0] = rgb[0];
      color[1] = rgb[1];
      color[2] = rgb[2];
      color[3] = 255;
    };
  }

  // Continuous (any numeric layer): rescale through the LUT. Absolute scales use the
  // authored range so 20 ppm P is the same yellow on every field; unmatched layers still
  // stretch across this layer's value_min..value_max.
  const lut = buildLut(layer);
  const abs = absoluteColorStops(layer);
  const min = abs && abs.length >= 2 ? abs[0]!.value : (layer.value_min ?? 0);
  const max =
    abs && abs.length >= 2
      ? abs[abs.length - 1]!.value
      : (layer.value_max ?? 1);
  const span = max - min || 1; // degenerate range -> single color, never divide by zero
  return (pixel, color, metadata) => {
    const raw = pixel[0];
    const nd = metadata.noData ?? layerNodata;
    if (raw == null || Number.isNaN(raw) || (nd !== undefined && raw === nd))
      return void color.set(TRANSPARENT);
    const v = raw * (metadata.scale ?? 1) + (metadata.offset ?? 0);
    let t = (v - min) / span;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    const i = Math.round(t * 255) * 3;
    color[0] = lut[i];
    color[1] = lut[i + 1];
    color[2] = lut[i + 2];
    color[3] = 255;
  };
}
